// The declaration behind a found-stock movement. The label below and the kardex deep link both
// need it, and they must not derive it two different ways.
export const getFoundStockDeclarationId = inv_history => inv_history?.referenceable?.id ?? inv_history?.referenceable_id

const FEFO_KEY = 'inventory_history_reference.fefo_override'

// A refill that stocked a pick face with a later lot while an earlier one was free in reserve.
// Names the lot that should have gone first, when that lot itself expires, where it was, and
// whether a warning was confirmed. Each part stands on its own: a reference missing one (an older
// row, a lot without a code) still shows the others.
//
// The date is the lot's own expiry, never `earliest_effective_expiration` (expiry minus the
// product's freshness), so it matches the handheld warning; rows that predate it show no date.
export const getFefoOverrideLabel = (inv_history, t) => {
  const reference = inv_history?.referenceable || {}
  const id = reference.id ?? inv_history?.referenceable_id
  const parts = [t(`${FEFO_KEY}.title`, { id: id ?? '' }).trim()]

  const earlier = [
    reference.earliest_lot_code && t(`${FEFO_KEY}.earlier_lot`, { lot: reference.earliest_lot_code }),
    reference.earliest_expiration_date && `(${t(`${FEFO_KEY}.expiring`, { date: reference.earliest_expiration_date })})`,
    reference.earliest_location_code && t(`${FEFO_KEY}.at_location`, { location: reference.earliest_location_code }),
  ].filter(Boolean)
  if (earlier.length) parts.push(earlier.join(' '))

  if (typeof reference.acknowledged === 'boolean') {
    parts.push(t(`${FEFO_KEY}.${reference.acknowledged ? 'acknowledged' : 'no_warning'}`))
  }
  return parts.join(' · ')
}

// Label for the "Id" column of an inventory-history row: what caused the movement, in the terms
// the operator recognizes, rather than the bare referenceable id. `t` translates the references
// that carry their own copy.
export const getReferenceIdOrOrderNumber = (inv_history, t) => {
  if (inv_history?.referenceable_type === 'Order') {
    return '#' + inv_history.referenceable?.order_number
  }
  // Accepting a not-received claim materializes the loss as LOST units and signs the movement with
  // whoever accepted it. Without naming the claim the row reads as an operator moving stock by
  // hand, which is exactly how these got reported as a compromised account.
  if (inv_history?.referenceable_type === 'Claim') {
    const claimId = inv_history.referenceable?.id ?? inv_history.referenceable_id
    if (claimId != null) return 'Disputa #' + claimId
  }
  if (inv_history?.referenceable_type === 'Replenishment' && inv_history?.action === 'Stock Transfer In') {
    const receiptId = inv_history.referenceable?.id ?? inv_history.referenceable_id
    if (receiptId != null) return 'Recibo #' + receiptId
  }
  // Settlement and declaration legs reference the FoundStockDeclaration; name it so the row reads
  // as surplus handling rather than a bare id.
  if (inv_history?.referenceable_type === 'FoundStockDeclaration') {
    const declarationId = getFoundStockDeclarationId(inv_history)
    if (declarationId != null) return 'Sobrante #' + declarationId
  }
  if (inv_history?.referenceable_type === 'FefoOverride') {
    return getFefoOverrideLabel(inv_history, t)
  }
  if (inv_history.referenceable_id === null) {
    return 'Sin registro'
  }
  return inv_history?.referenceable_id
}
